import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { MeasurementUnitModel } from '@/domains/measurement-units/types/models/MeasurementUnitModel.ts'
import type { StoreProductOptions } from '@/domains/products/store/use-product-store/ProductStore.types.ts'
import type { ProductModel } from '@/domains/products/types/models/ProductModel.types.ts'

import { useProductVariantStore } from '@/domains/products/store/use-product-variant-store/useProductVariantStore.ts'
import { DEFAULT_PRODUCT } from '@/domains/products/types/models/ProductModel.types.ts'

const DEFAULT_MEASUREMENT_UNIT_LABEL = 'Item'
const DEFAULT_VAT_RATE_LABEL = 'Full (18%)'

export const useProductStore = defineStore('product', () => {
  const _variantStore = useProductVariantStore()

  const options = ref<StoreProductOptions>({
    variantTypes: [],
    variantTypeOptions: {},
    taxonomies: [],
    brands: [],
    productClasses: [],
    pricingTypes: [],
    vatRates: [],
    productTypes: [],
    metaFields: [],
    fees: [],
    measurementUnits: [],
    availableSalesChannels: []
  })

  const duplicatedProduct = ref<ProductModel | undefined>()

  const createDefaultProduct = (): ProductModel => ({
    ...DEFAULT_PRODUCT(),
    variants: [_variantStore.defaults.variant()]
  })

  const defaultMeasurementUnit = computed((): MeasurementUnitModel => {
    const find = options.value.measurementUnits.find(item => item.label === DEFAULT_MEASUREMENT_UNIT_LABEL)

    let unitResult = {
      id: 0,
      name: ''
    }

    if (typeof find === 'undefined' || find.value === null) {
      return unitResult
    }

    unitResult = {
      id: Number(find.value),
      name: find.label
    }

    return unitResult
  })

  const defaultVatRateId = computed(() => {
    const vatRate = options.value.vatRates.find(rate => rate.label === DEFAULT_VAT_RATE_LABEL)?.value

    if (typeof vatRate === 'string' || typeof vatRate === 'number') {
      return Number(vatRate)
    }

    return null
  })

  const defaults = computed(() => {
    return {
      vatRate: {
        id: defaultVatRateId.value
      },
      measurementUnit: defaultMeasurementUnit.value,
      product: createDefaultProduct
    }
  })

  const setDuplicatedProduct = (product: ProductModel) => {
    duplicatedProduct.value = product
  }

  const consumeDuplicatedProduct = () => {
    const product = duplicatedProduct.value
    duplicatedProduct.value = undefined

    return product
  }

  const clearDuplicatedProduct = () => {
    duplicatedProduct.value = undefined
  }

  const hasDuplicatedProduct = computed(() => duplicatedProduct.value !== undefined)

  return {
    options,
    defaults,
    duplicatedProduct,
    setDuplicatedProduct,
    consumeDuplicatedProduct,
    clearDuplicatedProduct,
    hasDuplicatedProduct
  }
})
